// src/components/catalog/ContinentPackages.js
//
// Kıta seçicili tarife sayfası: Bölgesel ve Ön Ödemeli (ömürlük) sayfalarının
// ortak gövdesi. İkisi yalnızca hangi bundle'ları gösterdiğiyle ayrılır.
//
// Veri /v2/admin/bundles?continentId=… ucundan gelir. Eski /regions-data/{id} ve
// /lifetime-data/{id} uçları mobil uygulamaya hizmet ettiği için pasifleri
// döndürmüyor ve bundle id taşımıyor; o yüzden bu sayfalardan paket ne yayına
// alınabiliyor ne Play'e basılabiliyordu. Kıta → sağlayıcı bölgesi eşlemesi
// sunucuda: aynı kıta için iki uç aynı paket kümesini döndürür.

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import styles from './ContinentPackages.module.css';
import LoadingSpinner from '../common/LoadingSpinner';
import StatusToggle from '../common/StatusToggle';
import BundleControls from './BundleControls';
import {
    fetchAdminBundles,
    updateBundlePlan,
    buildPlanPatchFromEdit,
    readApiError,
    formatDataAmount,
    formatMoney,
    sortPlans,
} from '../../services/catalogApi';

// Sıra ekrandaki düğme sırası. Global'in id'si 0 — "boş" sayılmamalı.
// Global'i SİLMEYİN: global kapsamlı paketler (Hydra, Tucana, Eridanus,
// Lifetime Global 101 Countries) yalnızca buradan erişiliyor.
export const CONTINENTS = [
    { id: 1, name: 'Avrupa' },
    { id: 2, name: 'Asya' },
    { id: 3, name: 'Kuzey Amerika' },
    { id: 4, name: 'Latin Amerika' },
    { id: 5, name: 'Afrika' },
    { id: 7, name: 'Pasifik' },
    { id: 6, name: 'Orta Doğu' },
    { id: 0, name: 'Global' },
];

// Varsayılan süzgeç modül düzeyinde: varsayılan parametrede yazılan ok fonksiyonu
// her render'da yeniden oluşur ve listeyi sonsuz döngüyle yeniden çektirir.
const includeAll = () => true;

const isValidPrice = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) && n > 0;
};

// Fiyat alanı "199.90" gibi string gelir; input için string tutulur.
const asInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const planToEditState = (plan) => ({
    basePriceTry: asInputValue(plan.basePriceTry),
    isOpenGp: !!plan.isOpenGp,
    isOpenAp: !!plan.isOpenAp,
    isOpenWeb: !!plan.isOpenWeb,
});

/**
 * @param family        sunucuya giden aile filtresi
 * @param includeBundle yanıtı istemcide daraltır (örn. ülke paketlerini ayıklamak)
 */
const ContinentPackages = ({ family, includeBundle = includeAll, title, subtitle }) => {
    const [selectedContinentId, setSelectedContinentId] = useState(CONTINENTS[0].id);
    const [bundles, setBundles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Varsayılan: yalnızca aktifler. Fiyatı girilmiş ama henüz açılmamış paketi
    // bulup aktif etmek için açılır.
    const [showInactive, setShowInactive] = useState(false);

    const [editingStates, setEditingStates] = useState({}); // { planId: {…} }
    const [savingStates, setSavingStates] = useState({});   // { planId: bool }
    const [priceErrors, setPriceErrors] = useState({});     // { planId: true } → alanı kırmızıya boya

    const fetchBundles = useCallback(async (continentId) => {
        setIsLoading(true);
        setError(null);
        setBundles([]);
        try {
            const data = await fetchAdminBundles({
                continentId,
                family,
                includeInactive: showInactive ? undefined : false,
            });
            const visible = data.filter(includeBundle);
            setBundles(visible);

            const initialEdits = {};
            visible.forEach((bundle) => {
                (bundle.plans || []).forEach((plan) => {
                    initialEdits[String(plan.id)] = planToEditState(plan);
                });
            });
            setEditingStates(initialEdits);
            setPriceErrors({});
        } catch (err) {
            console.error(`Kıta ${continentId} için paketler çekilirken hata:`, err);
            setError(readApiError(err, 'Paketler yüklenirken bir hata oluştu.').message);
        } finally {
            setIsLoading(false);
        }
    }, [family, includeBundle, showInactive]);

    useEffect(() => {
        fetchBundles(selectedContinentId);
    }, [selectedContinentId, fetchBundles]);

    const handleInputChange = (planId, field, value) => {
        setEditingStates((prev) => ({ ...prev, [planId]: { ...prev[planId], [field]: value } }));
        // Kullanıcı alana dokunduğu anda kırmızı işaret kalkar.
        if (field === 'basePriceTry') {
            setPriceErrors((prev) => (prev[planId] ? { ...prev, [planId]: false } : prev));
        }
    };

    // Yayın kontrollerinden (aktif/pasif, Play) dönen alanları bundle'a işler.
    const mergeBundleIntoState = (bundleId, fields) => {
        setBundles((prev) => prev.map((bundle) => (bundle.id === bundleId ? { ...bundle, ...fields } : bundle)));
    };

    // Sunucudan dönen güncel planı listeye işler: fiyat kaydından sonra Google'ın
    // vergi dahil referans fiyatları ve priceId yenilenir, ekran da yenilenmeli.
    const mergePlanIntoState = (planId, nextPlan) => {
        setBundles((prev) =>
            prev.map((bundle) => ({
                ...bundle,
                plans: (bundle.plans || []).map((plan) =>
                    String(plan.id) === planId ? { ...plan, ...nextPlan } : plan
                ),
            }))
        );
    };

    const handleSaveChanges = async (plan) => {
        const planId = String(plan.id);
        const editedState = editingStates[planId];
        if (!editedState) return;

        const price = parseFloat(editedState.basePriceTry);
        if (isNaN(price) || price < 0) {
            setPriceErrors((prev) => ({ ...prev, [planId]: true }));
            toast.error('Lütfen geçerli bir fiyat girin.');
            return;
        }

        // Uç kısmi güncelleme yapar: yalnızca değişen alanlar gönderilir.
        const patch = buildPlanPatchFromEdit(editedState, planToEditState(plan));
        if (Object.keys(patch).length === 0) {
            toast.info('Değişiklik yok.');
            return;
        }

        setSavingStates((prev) => ({ ...prev, [planId]: true }));
        try {
            const updated = await updateBundlePlan(plan.id, patch);
            // Sunucu güncel planı döndürüyorsa onu kullan; döndürmüyorsa en azından
            // gönderdiğimiz alanları yansıt.
            const next = updated && updated.id ? updated : patch;
            mergePlanIntoState(planId, next);
            setEditingStates((prev) => ({ ...prev, [planId]: planToEditState({ ...plan, ...next }) }));
            toast.success(`Paket (ID: ${planId}) başarıyla güncellendi.`);
        } catch (err) {
            console.error(`Plan ${planId} güncellenirken API hatası:`, err);
            const { code, message } = readApiError(err, 'Paket güncellenirken bir hata oluştu.');
            if (code === 'INVALID_PRICE') {
                // Reddedilen değeri silmiyoruz: kullanıcı kendi girdisini düzeltecek.
                setPriceErrors((prev) => ({ ...prev, [planId]: true }));
            } else if (code !== 'STORE_PUSH_FAILED') {
                // STORE_PUSH_FAILED'de hiçbir alan yazılmadı; girdi kalsın ki tekrar
                // denenebilsin. Diğer hatalarda form sunucudaki değere döner.
                setEditingStates((prev) => ({ ...prev, [planId]: planToEditState(plan) }));
            }
            // Plan bulunamadıysa elimizdeki liste bayat: tazele.
            if (err?.response?.status === 404) fetchBundles(selectedContinentId);
            toast.error(message);
        } finally {
            setSavingStates((prev) => ({ ...prev, [planId]: false }));
        }
    };

    const selectedName = CONTINENTS.find((c) => c.id === selectedContinentId)?.name;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>{title}</h1>
                    <p className={styles.subtitle}>{subtitle}</p>
                </div>
                <div className={styles.infoNotes}>
                    <div className={styles.infoNote}>
                        <span className={styles.infoIcon}>ℹ️</span>
                        <span className={styles.infoText}>Girilen fiyatlar vergi hariçtir.</span>
                    </div>
                    <div className={styles.infoNote}>
                        <span className={styles.infoIcon}>ℹ️</span>
                        <span className={styles.infoText}>
                            Fiyat değişikliği Play'e kendiliğinden gider. "Play'e yayınla" yalnızca yeni bir
                            paketi ilk kez satışa açarken gerekir.
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.toolbar}>
                <div className={styles.continentSelector}>
                    {CONTINENTS.map((continent) => (
                        <button
                            key={continent.id}
                            className={`${styles.continentButton} ${selectedContinentId === continent.id ? styles.activeContinent : ''}`}
                            onClick={() => setSelectedContinentId(continent.id)}
                            disabled={isLoading}
                        >
                            {continent.name}
                        </button>
                    ))}
                </div>
                <StatusToggle
                    label="Pasif paketleri göster"
                    checked={showInactive}
                    disabled={isLoading}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    onText="Pasifler gösteriliyor"
                    offText="Pasifleri göster"
                />
            </div>

            <div className={styles.contentArea}>
                {isLoading ? (
                    <div className={styles.loadingContainer}><LoadingSpinner /></div>
                ) : error ? (
                    <div className={styles.errorPanel}>
                        <span className={styles.errorIcon}>⚠️</span>
                        <p className={styles.errorMessage}>{error}</p>
                        <button className={`${styles.retryButton} ghost`} onClick={() => fetchBundles(selectedContinentId)}>Tekrar dene</button>
                    </div>
                ) : bundles.length === 0 ? (
                    <p className={styles.emptyState}>
                        {selectedName} için gösterilecek {showInactive ? '' : 'aktif '}paket bulunamadı.
                        {!showInactive && ' Henüz yayına alınmamış bir paketi arıyorsanız “Pasifleri göster”i açın.'}
                    </p>
                ) : (
                    bundles.map((bundle) => (
                        <div key={bundle.id} className={styles.productCard}>
                            <div className={styles.productHeader}>
                                {bundle.imgUrl && (
                                    <img src={bundle.imgUrl} alt={bundle.title} className={styles.productImage} />
                                )}
                                <div className={styles.productInfo}>
                                    <h2 className={styles.productName}>{bundle.title}</h2>
                                    <p className={styles.productDescription}>{bundle.description}</p>
                                </div>
                                <BundleControls bundle={bundle} onUpdated={mergeBundleIntoState} />
                            </div>
                            <div className={styles.tableWrap}>
                                <table className={styles.refillsTable}>
                                    <thead>
                                        <tr>
                                            <th>Paket</th>
                                            <th className={styles.numCol}>Fiyat (TL)</th>
                                            <th className={styles.toggleCol}>Google Play</th>
                                            <th className={styles.toggleCol}>App Store</th>
                                            <th className={styles.toggleCol}>Web</th>
                                            <th className={styles.actionCol}>İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortPlans(bundle.plans || []).map((plan) => {
                                            const planId = String(plan.id);
                                            const currentEditState = editingStates[planId] || {};
                                            const isSavingThisRow = savingStates[planId] || false;
                                            // Taban fiyatı olmayan planın Play durumu değiştirilemez (PRICE_REQUIRED).
                                            const hasBase = isValidPrice(currentEditState.basePriceTry)
                                                || isValidPrice(plan.basePriceTry);

                                            return (
                                                <tr key={planId}>
                                                    <td>
                                                        <div className="num">{plan.title}</div>
                                                        <div className={styles.planMeta}>
                                                            <span className="num">{formatDataAmount(plan)}</span>
                                                            <span> · </span>
                                                            <span className="num">{plan.validityDays ?? '—'} gün</span>
                                                        </div>
                                                    </td>
                                                    <td className={styles.numCol}>
                                                        <input
                                                            type="number"
                                                            value={currentEditState.basePriceTry || ''}
                                                            onChange={(e) => handleInputChange(planId, 'basePriceTry', e.target.value)}
                                                            className={`${styles.priceInput} ${priceErrors[planId] ? styles.inputError : ''}`}
                                                            step="0.01"
                                                            disabled={isSavingThisRow}
                                                            aria-invalid={!!priceErrors[planId]}
                                                        />
                                                        <div className={styles.taxPrices} title="Vergi dahil satış fiyatları">
                                                            <span className={styles.taxLabel}>Vergi dahil</span>
                                                            <span className={`${styles.taxVals} num`}>₺{formatMoney(plan.priceTry)} · €{formatMoney(plan.priceEur)} · ${formatMoney(plan.priceUsd)}</span>
                                                        </div>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch} title={hasBase ? undefined : 'Önce fiyat girip kaydedin'}>
                                                            <input type="checkbox" checked={currentEditState.isOpenGp || false} onChange={(e) => handleInputChange(planId, 'isOpenGp', e.target.checked)} disabled={isSavingThisRow || !hasBase} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenAp || false} onChange={(e) => handleInputChange(planId, 'isOpenAp', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenWeb || false} onChange={(e) => handleInputChange(planId, 'isOpenWeb', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.actionCol}>
                                                        <button onClick={() => handleSaveChanges(plan)} className={styles.saveButton} disabled={isSavingThisRow}>
                                                            {isSavingThisRow ? 'Kaydediliyor...' : 'Kaydet'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ContinentPackages;
